import * as React from 'react';

import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license';

import { useGetCompanyByIdQuery } from '@/store/api/companyApi';
import { useGetCurrentUserQuery } from '@/store/api/userApi';
import { useAppSelector } from '@/store/hooks';

import { createAppTheme } from '@/theme';

import Navigation from './Navigation';
import { AuthLoading } from './components/auth/AuthLoading';
import { ErrorPage } from './components/common/ErrorPage';
import { LoadingIndicator } from './components/common/Loading';
import { useGlobalErrorHandler } from './hooks/useGlobalErrorHandler';
import { useLoadReferenceData } from './hooks/useLoadReferenceData';
import { AppRoutes } from './routes/AppRoutes';

import { appContentSx } from './App.styles';

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);

function App() {
  const { isLoading, error } = useGetCurrentUserQuery();
  const commonState = useAppSelector(state => state.common);
  const { darkMode } = commonState;
  useLoadReferenceData();
  useGlobalErrorHandler();

  const theme = createAppTheme(darkMode);

  const selectedCompany = useAppSelector(state => state.user.selectedCompany);
  const { data: company, isLoading: isCompanyLoading } = useGetCompanyByIdQuery(
    selectedCompany?.id || '',
    { skip: !selectedCompany?.id }
  );

  if (isLoading) return <AuthLoading />;
  if (error) return <ErrorPage />;
  if (isCompanyLoading || !company) return <LoadingIndicator />;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box>
        <Navigation />
        <Box component="main" id="appContent" sx={appContentSx()}>
          <AppRoutes />
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
